import './App.css';
import Modal from './modal';

/**
 * Renders salary schedule codes and related information based on provided props for final step in the questionnaire.
 * 
 * This component displays salary information including step codes, differentials,
 * and longevity codes. It also renders additional content and external links if provided.
 *
 * @param {Object} props - The props object containing the following properties:
 * @param {string} props.title - The title to be displayed for the salary schedule.
 * @param {string} props.stepCode - The step code used to determine the row and longevity information.
 * @param {string} props.differential - The differential code used to determine the column information.
 * @param {Array} [props.currentQuestion] - An optional array of content objects to be rendered.
 * @param {string} props.currentQuestion[].content - The HTML content to be rendered.
 * @param {string} [props.currentQuestion[].type] - The type of content, e.g., "External Link".
 * @param {string} [props.currentQuestion[].link] - The URL for external links.
 * @param {string} [props.currentQuestion[].label] - The label for external links.
 * 
 * @returns {JSX.Element} A React component displaying the formatted salary schedule information.
 */
function SalaryScheduleCodes(props) {
    let listContent;
    let convertedDifferential = '';
    let stepLetter = '';
    let stepNumber = '';
    let updatedStepLetter = '';
    let longevityCode = '+';
    let error = false;
    let listModal = [];
    if(props.jobTitle == "teacher" || props.jobTitle == "adult-education-teacher"){
        longevityCode = '+L';
    }
    if(props.jobTitle != "paraprofessionals"){
        stepLetter = props.stepCode.substring(1);
        stepNumber = props.stepCode.substring(0, 1);
    }

    // Format Longevity Date
    switch (stepLetter) {
        case "A":
            updatedStepLetter = 'A';
            longevityCode = '';
        break;
        case "B":
            updatedStepLetter = 'B';
            longevityCode = '';
        break;
        case "V":
            updatedStepLetter = 'A';
            longevityCode += "5";
        break;
        case "W":
            updatedStepLetter = 'B';
            longevityCode += "5";
        break;
        case "C":
            updatedStepLetter = 'A';
            longevityCode += "10";
        break;
        case "D":
            updatedStepLetter = 'B';
            longevityCode += "10";
        break;
        case "G":
            updatedStepLetter = 'A';
            longevityCode += "13";
        break;
        case "H":
            updatedStepLetter = 'B';
            longevityCode += "13";
        break;
        case "E":
            updatedStepLetter = 'A';
            longevityCode += "15";
        break;
        case "F":
            updatedStepLetter = 'B';
            longevityCode += "15";
        break;
        case "I":
            updatedStepLetter = 'A';
            longevityCode += "18";
        break;
        case "J":
            updatedStepLetter = 'B';
            longevityCode += "18";
        break;
        case "K":
            updatedStepLetter = 'A';
            longevityCode += "20";
        break;
        case "L":
            updatedStepLetter = 'B';
            longevityCode += "20";
        break;
        case "M":
            updatedStepLetter = 'A';
            longevityCode += "22";
        break;
        case "N":
            updatedStepLetter = 'B';
            longevityCode += "22";
        break;
    }

    // Make sure Lab Tech Is always 'A' after 5 years
    if(stepLetter != "B" && props.jobTitle == "lab-tech"){
        updatedStepLetter = 'A';
    }

    // Convert differentail to UFT Code
    switch (props.differential) {
        case "MA":
            convertedDifferential = "BA C1";
            break;
        case "1":
            convertedDifferential = "BA C1";
            break;
        case "PA":
            convertedDifferential = "BA+30<br/>C2";
            break;
        case "QA":
            convertedDifferential = "C2+ID";
            break;
        case "RA":
            convertedDifferential = "MA<br/>C2+PD";
            break;
        case "2":
            convertedDifferential = "MA<br/>C2+PD";
            break;
        case "SA":
            convertedDifferential = "C2+PD<br/>+ID";
            break;
        case "3":
            convertedDifferential = "C2+PD<br/>+ID";
            break;
        case "TA":
            convertedDifferential = "MA+30<br/>C6";
            break;
        case "4":
            convertedDifferential = "MA+30<br/>C6";
            break;
        case "UA":
            convertedDifferential = "MA+30<br/>C6+PD";
            break;
        case "5":
            convertedDifferential = "MA+30<br/>C6+PD";
            break;
        case "WA":
            convertedDifferential = "Base";
            break;
        case "WB":
            convertedDifferential = "Base+<br/>one diff";
            break;
        case "WE":
            convertedDifferential = "Base+<br/>two diff";
            break;
        case "WF":
            convertedDifferential = "Vlh";
            break;
        case "WH":
            convertedDifferential = "Vlh2";
            break;
        case "WT":
            convertedDifferential = "Base";
            break;
        case "WU":
            convertedDifferential = "Base + diff";
            break;
        case "WK":
            convertedDifferential = "Vlf+<br/>Vlk";
            break;
        case "WL":
            convertedDifferential = "Vlf2+<br/>Vlk2";
            break;
        case "WQ":
            convertedDifferential = "Vlf2+<br/>Vlk2";
            break;
        case "WM":
            convertedDifferential = "Vlf3+<br/>Vlk3";
            break;
        case "P1":
            convertedDifferential = "Teacher Aide";
            break;
        case "P2":
            convertedDifferential = "Ed Asst.";
            break;
        case "P3":
            convertedDifferential = "Ed. Asst. A-1";
            break;
        case "P4":
            convertedDifferential = "Ed. Asst. A-11";
            break;
        case "P5":
            convertedDifferential = "Ed. Asst. B";
            break;
        case "P6":
            convertedDifferential = "Ed. Assoc.";
            break;
        case "P9A":
            convertedDifferential = "Ed. Assoc. A/Aux. Trainer A";
            break;
        case "P9B":
            convertedDifferential = "Ed. Assoc. B/Aux. Trainer B";
            break;
        
    }

    // Map through step status object and set corresponding step status
    let stepStatus = Object.entries(props.stepStatus).map(([key, value]) => {
        if(key == 'step-1-status'){
            if(value == 'fail'){
                error = true;
            }
        }
        if(props.jobTitle == "paraprofessionals" && key == 'step-2-status'){
            if(value == 'fail'){
                error = true;
            }
        }
    })

    let salaryScheduleTitle = props.title;
    // Update Para headline if there is an error
    if(props.jobTitle == "paraprofessionals" && error == true){
        salaryScheduleTitle = 'You may be here on the NYC DOE salary schedule:';
    }

    // Render Modal
    const modals = props.modal;
    let row = stepNumber + updatedStepLetter + longevityCode;
    if(modals && Array.isArray(modals)){
        listModal = modals.map((modalContent, index) =>
            <Modal key={`modal-${index}`} currentQuestion={modalContent.content} label={modalContent.label} title={modalContent.title} isActive={props.isModalActive} setIsActive={props.setModalIsActive} row={row} column={convertedDifferential}/>
        )
    }

    // Render differentials based on the current differential
    const content = props.currentQuestion;
    if(content && Array.isArray(content)){
        listContent = content.map((content, index) =>
        <>
          {(content.type == "Content" && props.hoursPerWeek != "Y" && props.jobTitle == "adult-education-teacher") && <p className='content text-link-white'>The salary reported in this row/column is for someone who works 30 hours per week. {listModal} to convert the salary for your hours per week.</p>}
          <p className='content' dangerouslySetInnerHTML={{__html: content.content}}></p>
          {content.type == "External Link" && <a href={content.link} target="_blank" rel="noopener noreferrer" className="default-button next-button external-link" dangerouslySetInnerHTML={{__html: content.label}}></a>}
        </>
      );
    }

  return (
    <>
      <div className='content-box salary-schedule'>
        {props.adultEd == 'yes' && props.jobTitle == "paraprofessionals" ? <p className='content'>The NYC DOE salary schedule for paraprofessionals working in an adult education program is not available online. If you would like to confirm that your salary is correct, please call a UFT Salary Specialist at <a href="tel:212-331-6311" class='text-link white-text'>212-331-6311</a>.</p> : 
        <>
            <h3 className='title'>{salaryScheduleTitle}</h3>
            {error == true && <div className="alert-wrapper"><div className='alert warning'><p className='content'>Possible Error</p></div></div>}
            <div className='flex flex-row space-between'>
                <div>
                <p className='content'>ROW</p>
                    {props.jobTitle != 'paraprofessionals' ? <div className="pay-code-stub">
                        <p className='pay-code'>{stepNumber}{updatedStepLetter}{longevityCode}</p>
                    </div> : <div className="pay-code-stub full-width">
                        <p className='pay-code'>{convertedDifferential}</p>
                    </div>}
                </div>
                {(props.jobTitle != 'lab-tech' && props.jobTitle != 'paraprofessionals') &&  <div>
                    <p className='content'>COLUMN</p>
                    <div className="pay-code-stub">
                        <p className='pay-code' dangerouslySetInnerHTML={{__html: convertedDifferential}}></p>
                    </div>
                </div>}
            </div>
            {listContent}
        </>}
      </div>
    </>
  );
}

export default SalaryScheduleCodes;